import type {
  EarlyScreenEntry,
  EarlyScreenMark,
  EarlyScreenRecorder,
  ScreenHandle,
} from "@wishlink/applens-web";

type Mark = EarlyScreenMark extends infer M
  ? M extends { at: number }
    ? Omit<M, "at">
    : never
  : never;
type Slot = {
  entry: EarlyScreenEntry;
  marks: EarlyScreenMark[];
  state: "recording" | "transferred" | "discarded";
  finished: boolean;
};
const AGE = 60000;

/** App-owned, inert capture. No runtime SDK/dependencies, timers, listeners or I/O. */
export function createEarlyScreenRecorder(deps: {
  mayCapture(): boolean;
  clock: { now(): number; epochNow(): number };
  timeOrigin?(): number;
  requests(): ReturnType<EarlyScreenRecorder["drain"]>["requests"];
}) {
  let sealed = false;
  let slots: Slot[] = [];
  let last = -Infinity;
  function drop(slot: Slot) {
    slot.state = "discarded";
    slot.marks.length = 0;
  }
  function clear() {
    sealed = true;
    slots.forEach(drop);
    slots = [];
  }
  function time() {
    if (sealed) return;
    if (!deps.mayCapture()) {
      clear();
      return;
    }
    const now = deps.clock.now();
    if (!Number.isFinite(now) || now < 0 || now < last) {
      clear();
      return;
    }
    last = now;
    slots = slots.filter((slot) => {
      if (now - slot.entry.startMono <= AGE) return true;
      drop(slot);
      return false;
    });
    return now;
  }
  function copy(mark: Mark): Mark {
    const numbers = (input: object, keys: readonly string[]) =>
      Object.fromEntries(
        keys.flatMap((key) => {
          const value = (input as Record<string, unknown>)[key];
          return typeof value === "number" && Number.isFinite(value)
            ? [[key, value]]
            : [];
        })
      );
    switch (mark.kind) {
      case "loader":
        return { kind: mark.kind, action: mark.action };
      case "startType":
        return { kind: mark.kind, value: mark.value };
      case "launch":
        return {
          kind: mark.kind,
          value: numbers(mark.value, ["launchToContentMs", "launchToReadyMs"]),
        };
      case "documentTimings":
        return {
          kind: mark.kind,
          value: numbers(mark.value, [
            "documentToHomeStartMs",
            "documentToContentMs",
            "documentToReadyMs",
            "documentResponseEndMs",
            "documentDomReadyMs",
            "launchToDocumentMs",
          ]),
        };
      case "dependencies":
        return {
          kind: mark.kind,
          value: Object.fromEntries(
            ["creator_data", "creator_config", "intent"].flatMap((name) => {
              const query = mark.value[name];
              if (!query) return [];
              return [
                [
                  name,
                  {
                    ...numbers(query, [
                      "startedTimeStamp",
                      "fulfilledTimeStamp",
                    ]),
                    ...(typeof query.hasData === "boolean"
                      ? { hasData: query.hasData }
                      : {}),
                    ...([
                      "pending",
                      "fulfilled",
                      "rejected",
                      "uninitialized",
                    ].includes(query.status ?? "")
                      ? { status: query.status }
                      : {}),
                  },
                ],
              ];
            })
          ),
        };
      case "finish":
        return {
          kind: mark.kind,
          completion: {
            outcome: mark.completion.outcome,
            ...(mark.completion.errorCode === "home.blocking" ||
            mark.completion.errorCode === "home.partial"
              ? { errorCode: mark.completion.errorCode }
              : {}),
          },
          ...(mark.attributes
            ? {
                attributes: Object.fromEntries(
                  [
                    "app.dep.creator_data",
                    "app.dep.creator_config",
                    "app.dep.intent",
                  ].flatMap((key) => {
                    const value = mark.attributes?.[key];
                    return typeof value === "string" &&
                      ["absent", "cache", "inflight", "network"].includes(value)
                      ? [[key, value]]
                      : [];
                  })
                ),
              }
            : {}),
        };
    }
  }
  return {
    clear,
    start(key: string) {
      try {
        // Authorization precedes allocation and capture clock reads.
        const now = time();
        if (now === undefined || typeof key !== "string" || key.length > 128)
          return;
        const origin = deps.timeOrigin?.() ?? deps.clock.epochNow() - now;
        if (
          !Number.isFinite(origin) ||
          origin <= 0 ||
          !Number.isSafeInteger(Math.round(origin + now))
        )
          return;
        if (slots.length === 8) drop(slots.shift()!);
        const marks: EarlyScreenMark[] = [];
        const slot: Slot = {
          entry: {
            route: "home",
            key,
            timeOrigin: origin,
            startMono: now,
            marks,
          },
          marks,
          state: "recording",
          finished: false,
        };
        slots.push(slot);
        const mark = (value: Mark) => {
          try {
            if (slot.state !== "recording" || slot.finished) return false;
            const at = time();
            if (at === undefined || slot.state !== "recording") return false;
            if (marks.length === 32) {
              drop(slot);
              slots = slots.filter((item) => item !== slot);
              return false;
            }
            marks.push({ ...copy(value), at } as EarlyScreenMark);
            if (value.kind === "finish") slot.finished = true;
            return true;
          } catch {
            clear();
            return false;
          }
        };
        const handle: ScreenHandle = {
          loader: (action) => {
            mark({ kind: "loader", action });
          },
          setStartType: (value) => {
            mark({ kind: "startType", value });
          },
          setLaunch: (value) => {
            mark({ kind: "launch", value });
          },
          setDocumentTimings: (value) => {
            mark({ kind: "documentTimings", value });
          },
          setDependencies: (value) => {
            mark({ kind: "dependencies", value });
          },
          finish: (completion, attributes) =>
            mark({ kind: "finish", completion, attributes }),
        };
        return {
          handle,
          timestamp: Object.freeze({ timeOrigin: origin, startMono: now }),
          get recording() {
            try {
              return (
                time() !== undefined &&
                slot.state === "recording" &&
                !slot.finished
              );
            } catch {
              clear();
              return false;
            }
          },
          get transferred() {
            return slot.state === "transferred";
          },
        };
      } catch {
        clear();
        return;
      }
    },
    drain(): ReturnType<EarlyScreenRecorder["drain"]> {
      try {
        if (time() === undefined) return { screens: [] };
        sealed = true;
        const moved = slots;
        slots = [];
        moved.forEach((slot) => {
          slot.state = "transferred";
        });
        // No callbacks before screen ownership is sealed. Requests are removed once.
        return {
          requests: deps.requests(),
          screens: moved.map((slot) => slot.entry),
        };
      } catch {
        clear();
        return { screens: [] };
      }
    },
  };
}
